import React, { useEffect, useState } from 'react';
import 'styles/home.css';
import 'styles/articles.css';
import 'styles/doc-page.css';

import { SiteFooter, SiteNav, scrollToSection } from 'views/SiteChrome.js';
import QuickLookCta, { QuickLookRailLink } from 'views/QuickLookCta.js';
import DeepLookCta, { DeepLookButton, DeepLookStatus } from 'views/DeepLookCta.js';
import { DocSkeleton, usePlaceholder } from 'views/Loading.js';
import { locationForRoute } from 'files/routes.js';
import { arrangementCluster, processSteps } from 'files/rings.js';
import { inline, parseArticle, richBlock } from 'files/markdown.js';

/* Long-form pages that are documents rather than articles — How We Work and
   Ways to Work With Us, and whatever comes next.

   One component, one stylesheet, one markdown file per page. These pages were
   about to become two near-identical views, which is how the nav ended up with
   two copies that had drifted apart.

   Prose comes from /public through the same reader the articles use, so a page
   stays editable as a document. Two things are lifted out of the markdown as
   structure rather than prose:

   - a heading that starts "Step 3:" carries a step label. Counting instead would
     have numbered the headings that are not steps, like pricing and the summary.
   - a fully bold paragraph immediately under a heading is a kicker, not body
     copy. That is how the arrangements on the pricing page each state who they
     suit, and it is worth setting apart so the page can be skimmed by it. */

const STEP = /^Step\s+(\d+):\s*(.+)$/;
const KICKER = /^\*\*(.+)\*\*$/;

const readStep = text => {
    const match = STEP.exec(text);
    return match ? { label: match[1], title: match[2] } : { label: null, title: text };
}

/* Each node moves on its own terms: its own range of weight, its own period, and
   its own point in the cycle. Sharing one keyframe and one phase meant all three
   swelled to the same weight at the same moment, which is a lot of machinery to
   produce something that looks static. The periods are deliberately coprime-ish
   so the three never settle into a repeating pattern together. */
const NODE_MOTION = [
    { keyframes: 'doc-node-a', seconds: 54 },
    { keyframes: 'doc-node-b', seconds: 67 },
    { keyframes: 'doc-node-c', seconds: 60 }
];

// generated once at module load, not per render — the shapes must never change
const FIGURES = {
    arrangements: arrangementCluster(),
    steps: processSteps()
};

/* `sells` is the one thing that separates these pages from each other.
   Everything else here is content, but a page that takes money has a different
   call to action in three places — the header, the contents rail and the
   closing block — and all three have to agree about whether the reader has
   already bought. Naming the flow once is what keeps them agreeing. */
export const DOCS = {
    /* No `deep-look` entry. The offer page is views/DeepLook.js, because a page
       that sells is not a document and rendering it as one produced an article
       nobody could scan. The report below stays here, since three thousand
       words of findings genuinely is a document and this is the right shape
       for it.

       The sample report. Its own address rather than a section of the page that
       sells, because it is three thousand words of somebody else's findings and
       burying that inside the offer would make the offer unreadable. It is also
       the page most likely to be sent to somebody who never saw the offer at
       all, which is the same reason the report itself is written to stand
       alone. It sells too: a reader who got to the end of it is the warmest
       one there is. */
    'deep-look-report': {
        cta: 'Start a Deep Look',
        figure: 'steps',
        file: 'deep-look-report.md',
        rail: 'The findings',
        sells: true
    },
    'how-we-work': {
        cta: 'Take a Quick Look',
        figure: 'steps',
        file: 'how-we-work.md',
        rail: 'The process'
    },
    'ways-to-work': {
        cta: 'Take a Quick Look',
        figure: 'arrangements',
        file: 'ways-to-work.md',
        rail: 'The arrangements'
    },
    /* `legal`: a document someone reads to know where they stand, so it asks
       for nothing. No call to action in the header, the rail or the close, and
       no drawing. */
    privacy: {
        file: 'privacy.md',
        legal: true,
        rail: 'On this page'
    },
    terms: {
        file: 'terms.md',
        legal: true,
        rail: 'On this page'
    }
};

const DocPage = ({ doc, utils }) => {

    const config = DOCS[doc];

    const [content, setContent] = useState(null);
    const [failed, setFailed] = useState(false);

    // same beat the article page holds, for the same reason — see Loading.js
    const waiting = usePlaceholder(!content && !failed);

    useEffect(() => {
        document.body.classList.add('dl-body');
        return () => document.body.classList.remove('dl-body');
    }, []);

    useEffect(() => {

        let cancelled = false;
        setContent(null);
        setFailed(false);

        const run = async () => {
            try {
                const response = await fetch(`${process.env.PUBLIC_URL || ''}/${config.file}`);
                if(!response.ok) {
                    throw new Error(`${response.status}`);
                }
                const text = await response.text();
                if(!cancelled) {
                    setContent(parseArticle(text));
                }
            } catch(e) {
                if(!cancelled) {
                    setFailed(true);
                }
            }
        }
        run();

        return () => { cancelled = true; }

    }, [config.file]);

    const go = view => evt => {
        evt.preventDefault();
        utils.navigation.set({ view: view });
    }

    const onSection = id => evt => {
        evt.preventDefault();
        scrollToSection(id);
    }

    // everything before the first heading is the opening; the rest is the body
    const blocks = content ? content.blocks : [];
    const first = blocks.findIndex(block => block.type === 'h2');
    const opening = first === -1 ? blocks : blocks.slice(0, first);
    const body = first === -1 ? [] : blocks.slice(first);

    const sections = body
        .map((block, index) => ({ ...block, id: `part-${index}` }))
        .filter(block => block.type === 'h2')
        .map(block => ({ ...block, ...readStep(block.text) }));

    /* Where the rail's own row points on a page that sells. Resolved from the
       heading rather than written down as an id, because the ids are positions
       in the document and inserting a section above this one would silently
       point the link at whatever moved into its place. Undefined if the file
       has no such heading, and the rail then omits the row rather than
       rendering a link to nothing. */
    const priceSection = config.sells
        ? sections.find(section => /^what it costs/i.test(section.title))
        : null;

    /* The drawing sits in the header beside the statement. Line only, no labels,
       and the same weight cycle as every other drawing on the site. */
    const getFigure = () => {

        if(!config.figure) {
            return null;
        }

        if(config.figure === 'steps') {
            const drawing = FIGURES.steps;
            return (
                <figure aria-hidden={'true'} className={'doc-figure'}>
                    <svg fill={'none'} viewBox={'20 60 560 260'} xmlns={'http://www.w3.org/2000/svg'}>
                        {drawing.links.map(link => (
                            <path
                            d={link.d}
                            key={`link-${link.link}`}
                            stroke={'var(--sketch)'}
                            strokeLinecap={'round'}
                            strokeOpacity={0.42}
                            style={{ animationDelay: `${-link.phase * 60}s` }} />
                        ))}
                        {drawing.forms.map(form => (
                            <path
                            className={form.step === 0 ? 'is-accent' : ''}
                            d={form.d}
                            key={`form-${form.step}`}
                            stroke={'var(--sketch)'}
                            strokeOpacity={form.step === 0 ? 0.95 : 0.52}
                            style={{ animationDelay: `${-form.phase * 60}s` }} />
                        ))}
                    </svg>
                </figure>
            )
        }

        const cluster = FIGURES.arrangements;

        return (
            <figure aria-hidden={'true'} className={'doc-figure is-cluster'}>
                <svg fill={'none'} viewBox={'0 0 380 380'} xmlns={'http://www.w3.org/2000/svg'}>
                    {/* the shared form, in accent: whichever arrangement fits, this
                        is the part that does not change */}
                    {cluster.hub.map(arc => (
                        <path
                        /* held at a constant weight. The shared form is the one
                           thing on this page that does not change, and a line
                           that swells and thins says the opposite. */
                        className={'is-static'}
                        d={arc.d}
                        key={`arc-${arc.arc}`}
                        stroke={'var(--accent)'}
                        strokeLinecap={'round'}
                        strokeOpacity={0.85} />
                    ))}
                    {cluster.nodes.map(node => {
                        const motion = NODE_MOTION[node.node % NODE_MOTION.length];
                        return (
                            <path
                            d={node.d}
                            key={`node-${node.node}`}
                            stroke={'var(--sketch)'}
                            strokeOpacity={0.62}
                            style={{
                                animationDelay: `${-node.phase * motion.seconds}s`,
                                animationDuration: `${motion.seconds}s`,
                                animationName: motion.keyframes
                            }} />
                        )
                    })}
                </svg>
            </figure>
        )
    }

    const getRail = () => {
        return (
            <aside className={'ar-rail'}>
                <nav className={'ar-rail-box'}>
                    <span className={'ar-rail-title'}>{config.rail}</span>
                    <ol className={'ar-rail-list doc-rail-list'}>
                        {sections.map(section => (
                            <li key={section.id}>
                                <a href={`#${section.id}`} onClick={onSection(section.id)}>
                                    {section.label ? `${section.label}. ` : ''}{section.title}
                                </a>
                            </li>
                        ))}
                        {/* A page that sells does not put the free tool in its
                            contents rail. The rail follows the reader down the
                            whole page, so an invitation to go and do something
                            else is a second call to action competing with the
                            one the page exists for. The sections are already
                            listed above this, so the rail's own row is spent on
                            the question somebody scrolling a priced page is
                            actually holding. */}
                        {config.sells
                            ? priceSection && (
                                  <li className={'ar-rail-aside'}>
                                      <a href={`#${priceSection.id}`} onClick={onSection(priceSection.id)}>
                                          <span>What it costs <span className={'dl-arrow'}>→</span></span>
                                      </a>
                                  </li>
                              )
                            : !config.legal && <QuickLookRailLink utils={utils} />}
                    </ol>
                </nav>
            </aside>
        )
    }

    const getBody = () => {
        return body.map((block, index) => {

            if(block.type === 'h2') {
                const step = readStep(block.text);
                return (
                    <h2 className={'doc-heading'} id={`part-${index}`} key={index}>
                        {step.label && <span className={'doc-step'}>Step {step.label}</span>}
                        <span>{inline(step.title)}</span>
                    </h2>
                )
            }
            if(block.type === 'h3') return <h3 key={index}>{inline(block.text)}</h3>;

            const rich = richBlock(block, index);
            if(rich) return rich;

            // a fully bold paragraph under a heading states who the section suits
            const kicker = KICKER.exec(block.text);
            if(kicker && body[index - 1] && body[index - 1].type === 'h2') {
                return <p className={'doc-kicker'} key={index}>{kicker[1]}</p>;
            }
            return <p key={index}>{inline(block.text)}</p>;
        });
    }

    /* The status provider wraps the whole page rather than each button, so the
       header and the closing block are drawing from one answer. Two lookups
       would be two answers in flight at once, which shows up as a header
       offering to sell a pass the foot of the page has already recognised.

       A page that does not sell gets the same tree without the provider, since
       it has nothing to ask about. */
    const page = (
        <div className={'dl'}>
            <SiteNav utils={utils} />

            <section className={'doc-head'}>
                <div className={'dl-wrap doc-head-grid'}>
                    <div>
                        <h1 className={'dl-display'}>{content ? content.title : ''}</h1>
                        {content && content.dek && <p className={'doc-dek'}>{content.dek}</p>}
                        {config.legal ? null : config.sells ? (
                            <DeepLookButton label={config.cta} utils={utils} />
                        ) : (
                            <a className={'dl-btn'} href={locationForRoute({ view: 'quick-look' })} onClick={go('quick-look')}>
                                {config.cta} <span className={'dl-arrow'}>→</span>
                            </a>
                        )}
                    </div>
                    {getFigure()}
                </div>
            </section>

            <section className={'doc-body'}>
                <div className={'dl-wrap'}>

                    {waiting && <DocSkeleton />}

                    {!waiting && failed && (
                        <div className={'ar-state'}>
                            <h2>We could not load this page.</h2>
                            <p>Refreshing usually sorts it out. If it does not, email hello@duralabs.io and we will walk you through it.</p>
                        </div>
                    )}

                    {!waiting && content && (
                        <div className={'ar-shell is-railed ar-arrived'}>

                            {/* The overview sits where an article's title and dek
                                sit: in .ar-head, which spans both grid columns in
                                row one and carries the rule beneath it. The rail
                                and the sections then begin together in row two.

                                That placement is the whole point. The contents
                                list is a list of the sections, so it should start
                                where the sections start, not a screen above them
                                beside an overview it does not describe.

                                One column, not two. The version this replaced put
                                the first paragraph on the left and the rest on the
                                right, which filled the rail's own slot with body
                                copy and made an overview read as a separate device
                                rather than as the opening of the document. The
                                right side of a header being quiet is the house
                                pattern, the same way a dek sits at reading measure
                                under a full-width title. */}
                            {opening.length > 0 && (
                                <header className={'ar-head doc-opening'}>
                                    {/* Rich blocks here too, not just paragraphs.
                                        The opening is whatever sits above the
                                        first heading, and on How We Work that is
                                        now the numbered summary of the five
                                        steps — the answer to the question the
                                        page exists for, which belongs above the
                                        fold rather than under a heading. */}
                                    {opening.map((block, index) => richBlock(block, index) || (
                                        <p className={index === 0 ? 'doc-lede' : ''} key={index}>
                                            {inline(block.text)}
                                        </p>
                                    ))}
                                </header>
                            )}

                            {getRail()}

                            <div className={'ar-prose'}>
                                <div className={'ar-body doc-prose'}>{getBody()}</div>

                                {content.callout && !config.legal && (
                                    config.sells
                                        ? <DeepLookCta callout={content.callout} utils={utils} />
                                        : <QuickLookCta callout={content.callout} utils={utils} />
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </section>

            <SiteFooter utils={utils} />
        </div>
    )

    return config.sells
        ? <DeepLookStatus utils={utils}>{page}</DeepLookStatus>
        : page;
}

export default DocPage;
